import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { healthAPI } from '../services/api';
import { Activity, Droplets, Flame, Dumbbell, UtensilsCrossed, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useLocale } from '../context/LocaleContext';

const HealthCalendar = () => {
  const { t } = useLocale();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [monthlyData, setMonthlyData] = useState({});

  useEffect(() => {
    loadMonthlyData(selectedDate.getFullYear(), selectedDate.getMonth() + 1);
  }, [selectedDate.getMonth(), selectedDate.getFullYear()]);

  const loadMonthlyData = async (year, month) => {
    try {
      const response = await healthAPI.getMonthlySummary(year, month);
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    }
  };

  const loadHealthDataForDate = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await healthAPI.getHealthHistory(dateStr);
      setHealthData(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    loadHealthDataForDate(date);
  };

  // Create arrays of dates for each modifier
  const getDatesWithData = () => {
    if (!monthlyData.data_by_date) return [];
    const dates = [];
    Object.entries(monthlyData.data_by_date).forEach(([dateStr, data]) => {
      if (data.has_data) {
        dates.push(new Date(dateStr));
      }
    });
    return dates;
  };

  const modifiers = {
    hasData: getDatesWithData()
  };

  const modifiersStyles = {
    hasData: { fontWeight: 'bold', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '0.375rem' }
  };

  const handleMonthChange = (month) => {
    setSelectedDate(month);
    loadMonthlyData(month.getFullYear(), month.getMonth() + 1);
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden px-1">
      <style>{`
        /* Enhanced Health Calendar Styling */
        .health-calendar-enhanced .rdp {
          --rdp-cell-size: 50px;
          --rdp-accent-color: #10b981;
          --rdp-background-color: #d1fae5;
        }
        
        .health-calendar-enhanced .rdp-day {
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .health-calendar-enhanced .rdp-day:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }
        
        .health-calendar-enhanced .has-data-indicator {
          position: relative;
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          font-weight: 600;
          border: 2px solid #10b981;
        }
        
        .health-calendar-enhanced .has-data-indicator::after {
          content: '💪';
          position: absolute;
          bottom: -2px;
          right: -2px;
          font-size: 12px;
        }
        
        .health-calendar-enhanced .rdp-day_selected {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          font-weight: bold;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.5);
        }
        
        .health-calendar-enhanced .rdp-head_cell {
          color: #059669;
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
        }
        
        .health-calendar-enhanced .rdp-caption {
          color: #059669;
          font-weight: 700;
          font-size: 1.125rem;
        }
      `}</style>
      
      {/* Enhanced Monthly Summary with Gradients */}
      {monthlyData.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">{t('home.workouts')}</p>
                  <p className="text-2xl font-bold text-blue-900">{monthlyData.summary.total_workouts || 0}</p>
                  <p className="text-xs text-blue-500 mt-1">🎯 {t('health.keepMoving')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-cyan-100 rounded-xl group-hover:bg-cyan-200 transition-colors">
                  <Droplets className="h-6 w-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-xs text-cyan-600 font-semibold uppercase tracking-wide">{t('health.avgWater')}</p>
                  <p className="text-2xl font-bold text-cyan-900">{monthlyData.summary.avg_water} {t('health.glasses')}</p>
                  <p className="text-xs text-cyan-500 mt-1">💧 {t('health.stayHydrated')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-orange-100 rounded-xl group-hover:bg-orange-200 transition-colors">
                  <Flame className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">{t('health.avgCalories')}</p>
                  <p className="text-2xl font-bold text-orange-900">{monthlyData.summary.avg_calories}</p>
                  <p className="text-xs text-orange-500 mt-1">🔥 {t('health.burnIt')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-teal-100 rounded-xl group-hover:bg-teal-200 transition-colors">
                  <UtensilsCrossed className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide">{t('health.daysTracked')}</p>
                  <p className="text-2xl font-bold text-teal-900">{monthlyData.total_days || 0}</p>
                  <p className="text-xs text-teal-500 mt-1">📅 {t('health.keepLogging')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Calendar with Visual Indicators */}
      <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 shadow-xl">
        <CardHeader className="border-b border-green-100 bg-white/50 backdrop-blur px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
              <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg sm:text-2xl bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent truncate">
                {t('health.healthCalendar')}
              </CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                {t('health.trackYourHealthJourney')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex justify-center w-full overflow-x-auto p-4">
            <div className="health-calendar-enhanced">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                onMonthChange={handleMonthChange}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                modifiersClassNames={{
                  hasData: 'has-data-indicator'
                }}
                className="rounded-xl border-2 border-green-200 bg-white shadow-lg p-4"
              />
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-6 text-xs sm:text-sm px-2">
            <div className="flex items-center gap-1.5 sm:gap-2 bg-green-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full border border-green-200">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex-shrink-0"></div>
              <span className="text-gray-700 font-medium whitespace-nowrap">{t('life.hasData')}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 bg-gray-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full border border-gray-200">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gray-200 flex-shrink-0"></div>
              <span className="text-gray-700 font-medium whitespace-nowrap">{t('life.noData')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('health.healthDataFor')} {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : healthData ? (
            <div className="space-y-6">
              {/* Activity Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Droplets className="h-8 w-8 mx-auto mb-2 text-cyan-500" />
                    <p className="text-sm text-gray-500">{t('health.waterIntake')}</p>
                    <p className="text-2xl font-bold">{healthData.water} {t('common.glasses')}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6 text-center">
                    <Flame className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                    <p className="text-sm text-gray-500">{t('health.burned')}</p>
                    <p className="text-2xl font-bold">
                      {(healthData.workouts || []).reduce((total, workout) => total + (workout.calories || 0), 0)} {t('common.cal')}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Nutrition */}
              {healthData.nutrition && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <UtensilsCrossed className="h-5 w-5 mr-2" />
                      {t('health.nutrition')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">{t('health.caloriesIntake')}</p>
                        <p className="text-xl font-bold">{healthData.nutrition.calories_intake}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('life.protein')}</p>
                        <p className="text-xl font-bold">{healthData.nutrition.protein}g</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('life.carbs')}</p>
                        <p className="text-xl font-bold">{healthData.nutrition.carbs}g</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('life.fat')}</p>
                        <p className="text-xl font-bold">{healthData.nutrition.fat}g</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Workouts */}
              {healthData.workouts && healthData.workouts.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Dumbbell className="h-5 w-5 mr-2" />
                      {t('health.workoutsCount')} ({healthData.workouts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {healthData.workouts.map((workout, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{workout.type}</p>
                            <p className="text-sm text-gray-500">{workout.duration} {t('health.minutes')}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-orange-500">{workout.calories} {t('common.cal')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Foods */}
              {healthData.foods && healthData.foods.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <UtensilsCrossed className="h-5 w-5 mr-2" />
                      {t('health.mealsCount')} ({healthData.foods.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {healthData.foods.map((food, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{food.name}</p>
                            {food.serving_size && (
                              <p className="text-sm text-gray-500">{food.serving_size}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{food.calories} {t('common.cal')}</p>
                            <p className="text-xs text-gray-500">
                              P: {food.protein}g C: {food.carbs}g F: {food.fat}g
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No Data */}
              {healthData.water === 0 && 
               (!healthData.workouts || healthData.workouts.length === 0) &&
               (!healthData.foods || healthData.foods.length === 0) && (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <p>{t('health.noHealthData')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealthCalendar;
